import React, { useEffect, useState } from "react";
import withRedirect from "../../../helpers/hoc/withRedirect";
import { fetchWithAuthorization } from "../../../helpers/api/fetch";
import { Alert, Card, CardBody, Col, Form, Row } from "reactstrap";
import ProfileBanner from "./ProfileBanner";
import ProfileBasic from "./ProfileBasic";
import ProfileContact from "./ProfileContact";
import { useDispatch, useSelector } from "react-redux";
import ProfileAddress from "./ProfileAddress";
import { Toast } from "./../../UI/toast/Toast";
import { getErrorMessage } from "../../../helpers/errors/handleErrorMessage";
import { updatedUserProfile, updateMyProperties } from "../../../actions/authActions";
import { RenameSelectedProperty } from "../../../actions/propertyActions";
import ButtonIcon from "../../common/ButtonIcon";
import { useForm } from "react-hook-form";
import { checkUserToken } from "../../../helpers/api/auth";
import { Or, PreloadState } from "../../UI/preloading/PreloadState";
import Loader from "../../common/Loader";
import ProfilePassword from "./ProfilePassword";
import ProfileAccountManagement from "./ProfileAccountManagement";
import ProfileReferrals from "./ProfileReferrals";
import { REWARDS_ENABLED } from "../../../helpers/rewardsEnabled";

const MyProfile = () => {
  const { token, role, hasMultipleProperties } = useSelector((state) => state.auth.user);
  const { selectedProperty } = useSelector((state) => state.property);
  const isOwner = role === "property";
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [wasBeEdited, setWasBeEdited] = useState(false);
  const [userData, setUserData] = useState({});
  const [username, setUsername] = useState("");
  const [property, setProperty] = useState("");
  const [authId, setAuthId] = useState(null);

  const {
    handleSubmit,
    register,
    setValue,
    setError: setFieldError,
    clearErrors: clearFieldErrors,
    formState: { errors },
  } = useForm();

  const loadData = () => {
    setUserData({});
    setLoading(true);
    return checkUserToken(token)
      .then((profile) => {
        const { id, username, data, property } = profile || {};
        setAuthId(id);
        setUsername(username);
        setUserData(data);
        setProperty(property);
        // HO-ISSUE-119: al renombrar el inmueble, el navbar seguia mostrando
        // el nombre viejo. Se toma el que devuelve el backend (el de la unidad
        // seleccionada, ya guardado) y no lo que se escribio en el formulario,
        // para que la etiqueta diga siempre lo que quedo en la base.
        if (property) {
          dispatch(RenameSelectedProperty(property));
        }
        // Y la lista de propiedades vinculadas, para que "Cambiar de
        // propiedad" no siga mostrando el nombre anterior.
        if (profile?.properties) {
          dispatch(updateMyProperties(profile));
        }
      })
      .catch((error) => setError(error))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
    return () => setUserData({});
    // eslint-disable-next-line
  }, []);

  const updateData = async (e) => {
    if (submitting) return;
    setSubmitting(true);
    clearFieldErrors();
    const payload = isOwner ? { email: e.email, phone: e.phone } : e;
    try {
      await fetchWithAuthorization(
        `/profile/${authId}`,
        payload,
        "PUT"
      );
      dispatch(updatedUserProfile(isOwner ? {
        ...payload,
        firstname: userData.firstname,
        lastname: userData.lastname,
      } : payload));
      await loadData();
      setError(null);
      Toast.fire({
        type: "success",
        title: "Datos actualizados con exito.",
      });
    } catch (e) {
      setError(e);
      Object.entries(e?.errors || {}).forEach(([field, messages]) => {
        setFieldError(field, { type: "server", message: messages[0] });
      });
    } finally {
      setWasBeEdited(false);
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (error !== null && error.errors?.email?.[0]) {
      Toast.fire({
        type: "error",
        title: error.errors.email[0],
      });
    } else if (error !== null) {
      Toast.fire({
        type: "error",
        title: getErrorMessage(error?.code),
      });
    }
  }, [error]);

  return (
    <>
      {userData ? (
        <ProfileBanner
          edit={!loading && !submitting}
          loadData={loadData}
          image={userData.image}
          address={userData.address}
          firstname={userData.firstname || "... "}
          lastname={userData.lastname || " ..."}
        />
      ) : (
        <Loader />
      )}
      {error && (
        <Alert
          closeAriaLabel="x"
          onClick={(e) => setError(null)}
          color="warning"
          className="mb-3"
        >
          {error.message || error.errors?.email?.[0]}
        </Alert>
      )}

      <Card className="mb-3 bg-light">
        <CardBody>
          <PreloadState cond={!!userData.id} loading={loading}>
            <Or value={true}>
              <Form onSubmit={handleSubmit(updateData)}>
                <ProfilePassword data={userData} username={username} />
                <ProfileBasic
                  wasBeEdited={() => setWasBeEdited(true)}
                  edit={!isOwner && !loading && !submitting}
                  register={register}
                  setValue={setValue}
                  errors={errors}
                  dni={userData?.dni}
                  firstname={userData?.firstname}
                  lastname={userData?.lastname}
                  property={property}
                />
                {isOwner && (
                  <p className="text-muted fs--1 mx-3 mb-0">
                    Solo puedes actualizar tu foto, contraseña, correo electrónico
                    y teléfono. Para cambiar los demás datos, contacta a la
                    administración.
                  </p>
                )}
                <ProfileContact
                  wasBeEdited={() => setWasBeEdited(true)}
                  edit={!loading && !submitting}
                  register={register}
                  setValue={setValue}
                  errors={errors}
                  phone={userData?.phone}
                  email={userData?.email}
                />

                <Row>
                  <Col className="d-flex justify-content-end align-items-center mt-3">
                    <ButtonIcon
                      data-testid="submit-button"
                      color="primary"
                      className="px-5"
                      type="submit"
                      icon="save"
                      disabled={loading || submitting || !wasBeEdited}
                      transform="down-1 shrink-4"
                    >
                      Actualizar
                    </ButtonIcon>
                  </Col>
                </Row>
              </Form>
            </Or>
            <Or value={false}>
              <></>
            </Or>
          </PreloadState>
        </CardBody>
      </Card>

      {/* HO-TASK-286: el codigo de referido es de la propiedad activa. */}
      {REWARDS_ENABLED && role === "property" && (!hasMultipleProperties || selectedProperty) && (
        <ProfileReferrals />
      )}

      <ProfileAccountManagement email={userData?.email} />
    </>
  );
};

export default withRedirect(MyProfile);
